// import { HorseService } from '@pingum/api-services';
import FileDownload from 'react-file-download';
import HorsePublicShareService from '../../services/HorsePublicShareService';
import HorseService from '../../services/HorseService';
import LocationService from '../../services/LocationService';
import SuggestionService from '../../services/SuggestionService';
import { getAllHorseLocations } from '../location/actions';
import {
  dispatchNetworkRequest,
  dispatchNetworkRequestDynamicSuccessDispatch,
} from '../networkDispatcher';
import * as types from './types';

const previewableFileRegex = /.png|jpeg|jpg|gif$/;

/* *********
  General Horse Methods
********** */

const getAllPagedHorses = async (page, pageSize, allContent = []) => {
  const response = await HorseService.getHorses(page, pageSize);
  const newAllContent = [...allContent, ...response.content];

  if (response.last) { return newAllContent; }
  return getAllPagedHorses(page + 1, pageSize, newAllContent);
};

export const getHorse = (id) => (dispatch) => {
  const promise = HorseService.getHorse(id);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_HORSE_REQUESTED,
    successType: types.GET_HORSE_SUCCESS,
    errorType: types.GET_HORSE_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem fetching the horse profile',
    responseName: 'horse',
  });
  return promise;
};

export const getHorses = () => (dispatch) => {
  dispatch({ type: types.GET_HORSES_REQUESTED });

  const promise = getAllPagedHorses(0, 50);

  promise.then((response) => {
    dispatch({
      type: types.GET_HORSES_SUCCESS,
      horses: response,
    });
  }).catch((error) => {
    console.error(error);
    dispatch({
      type: types.GET_HORSES_ERROR,
      message: 'There was an error getting horses at this barn. Please contact Questri support.',
    });
  });
};

export const updateHorse = (id, request) => (dispatch) => {
  const promise = HorseService.updateHorse(id, request);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.UPDATE_HORSE_REQUESTED,
    successType: types.UPDATE_HORSE_SUCCESS,
    errorType: types.UPDATE_HORSE_ERROR,
    promise,
    successMessage: 'Successfully updated the horse',
    defaultErrorMessage: 'There was a problem updating the horse',
    responseName: 'updatedHorse',
  });
  return promise;
};

/* *********
  Barn Horse (Lodging) Methods
********** */

export const getLodgingsForHorse = (id, barnId) => (dispatch) => {
  const promise = HorseService.getLodgingsForHorse(id, barnId);
  dispatchNetworkRequestDynamicSuccessDispatch({
    dispatch,
    requestType: types.GET_HORSE_LODGINGS_REQUESTED,
    errorType: types.GET_HORSE_LODGINGS_ERROR,
    successDispatch: {
      type: types.GET_HORSE_LODGINGS_SUCCESS,
      selectedBarnId: barnId,
    },
    promise,
    defaultErrorMessage: 'There was a problem fetching lodging information for this horse',
    responseName: 'horseLodgings',
  });
  return promise;
};

export const getAllLodgingsForHorse = (id, page = 0, pageSize = 25, sort = '') => (dispatch) => {
  const promise = HorseService.getAllLodgingsForHorse(id, page, pageSize, sort);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_ALL_HORSE_LODGINGS_REQUESTED,
    successType: types.GET_ALL_HORSE_LODGINGS_SUCCESS,
    errorType: types.GET_ALL_HORSE_LODGINGS_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem fetching all lodging information for this horse',
    responseName: 'allHorseLodgings',
  });
  return promise;
};

/* *********
  Horse Note Methods
********** */

export const getHorseNotes = (id, page = 0, pageSize = 25, sort = '', type = null, noteTypes = null) => (dispatch) => {
  const promise = HorseService.getHorseNotes(id, page, pageSize, sort, type, noteTypes);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_HORSE_NOTES_REQUESTED,
    successType: types.GET_HORSE_NOTES_SUCCESS,
    errorType: types.GET_HORSE_NOTES_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem loading notes for this horse',
    responseName: 'horseNotes',
  });
  // get preview image for any images
  promise.then((result) => {
    if (result.content && result.content.length > 0) {
      // for any notes with image files, get previews
      for (let i = 0; i < result.content.length; i += 1) {
        if (result.content[i].information && result.content[i].information.files && result.content[i].information.files.length > 0) {
          for (let j = 0; j < result.content[i].information.files.length; j += 1) {
            if (result.content[i].information.files[j].match(previewableFileRegex)) {
              const fileDownloadPromise = HorseService.getHorseFile(id, result.content[i].id, result.content[i].information.files[j]);
              fileDownloadPromise.then((download) => {
                const blob = new Blob([download], { type: 'application/octet-stream' });
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                  const base64data = reader.result;
                  dispatch({
                    type: types.SET_HORSE_FILE_PREVIEWS,
                    noteId: result.content[i].id,
                    file: result.content[i].information.files[j],
                    base64data,
                  });
                };
              });
            }
          }
        }
      }
    }
  });
  return promise;
};

export const getHorseNoteHighlights = (id, hideRequest = false) => (dispatch) => {
  const promise = HorseService.getHorseNoteHighlights(id);
  dispatchNetworkRequest({
    dispatch,
    requestType: hideRequest
      ? 'DO_NOT_REQUEST'
      : types.GET_HORSE_NOTE_HIGHLIGHTS_REQUESTED,
    successType: types.GET_HORSE_NOTE_HIGHLIGHTS_SUCCESS,
    errorType: types.GET_HORSE_NOTE_HIGHLIGHTS_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem loading note highlights for this horse',
    responseName: 'horseNoteHighlights',
  });
  // get preview image for any images
  promise.then((result) => {
    if (result && result.length > 0) {
      // for any notes with image files, get previews
      for (let i = 0; i < result.length; i += 1) {
        if (result[i].information && result[i].information.files && result[i].information.files.length > 0) {
          for (let j = 0; j < result[i].information.files.length; j += 1) {
            if (result[i].information.files[j].match(previewableFileRegex)) {
              const fileDownloadPromise = HorseService.getHorseFile(id, result[i].id, result[i].information.files[j]);
              fileDownloadPromise.then((download) => {
                const blob = new Blob([download], { type: 'application/octet-stream' });
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                  const base64data = reader.result;
                  dispatch({
                    type: types.SET_HORSE_FILE_PREVIEWS,
                    noteId: result[i].id,
                    file: result[i].information.files[j],
                    base64data,
                  });
                };
              });
            }
          }
        }
      }
    }
  });
  return promise;
};

export const addHorseNote = (id, request) => (dispatch) => {
  const promise = HorseService.addHorseNote(id, request);
  dispatchNetworkRequestDynamicSuccessDispatch({
    dispatch,
    requestType: types.ADD_HORSE_NOTE_REQUESTED,
    successDispatch: {
      type: types.ADD_HORSE_NOTE_SUCCESS,
      pinToHorse: request.setAsHorseActiveNote || false,
    },
    errorType: types.ADD_HORSE_NOTE_ERROR,
    promise,
    successMessage: 'Successfully created the note',
    defaultErrorMessage: 'There was a problem adding the note for this horse',
    responseName: 'newHorseNote',
  });
  return promise;
};

export const updateHorseNote = (id, noteId, request) => (dispatch) => {
  const promise = HorseService.updateHorseNote(id, noteId, request);
  dispatchNetworkRequestDynamicSuccessDispatch({
    dispatch,
    requestType: types.UPDATE_HORSE_NOTE_REQUESTED,
    successDispatch: {
      type: types.UPDATE_HORSE_NOTE_SUCCESS,
      pinToHorse: request.setAsHorseActiveNote || false,
    },
    errorType: types.UPDATE_HORSE_NOTE_ERROR,
    promise,
    successMessage: 'Successfully updated the note',
    defaultErrorMessage: 'There was a problem updating the note',
    responseName: 'updatedHorseNote',
  });
  return promise;
};

export const deleteHorseNote = (id, noteId) => (dispatch) => {
  const promise = HorseService.deleteHorseNote(id, noteId);
  dispatchNetworkRequestDynamicSuccessDispatch({
    dispatch,
    requestType: types.DELETE_HORSE_NOTE_REQUESTED,
    successDispatch: {
      type: types.DELETE_HORSE_NOTE_SUCCESS,
      deletedHorseNoteId: noteId,
    },
    errorType: types.DELETE_HORSE_NOTE_ERROR,
    promise,
    successMessage: 'Successfully deleted the note',
    defaultErrorMessage: 'There was a problem deleting the note',
    responseName: 'response',
  });
  return promise;
};

/* *********
  Horse Feed Methods
********** */

export const getHorseFeeds = (id, page = 0, pageSize = 25, sort = '') => (dispatch) => {
  const promise = HorseService.getHorseFeeds(id, page, pageSize, sort);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_HORSE_FEEDS_REQUESTED,
    successType: types.GET_HORSE_FEEDS_SUCCESS,
    errorType: types.GET_HORSE_FEEDS_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem loading feeds for this horse',
    responseName: 'horseFeeds',
  });
  return promise;
};

export const addHorseFeed = (id, feedId, request) => (dispatch) => {
  const promise = HorseService.addHorseFeed(id, feedId, request);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.ADD_HORSE_FEED_REQUESTED,
    successType: types.ADD_HORSE_FEED_SUCCESS,
    errorType: types.ADD_HORSE_FEED_ERROR,
    promise,
    successMessage: 'Successfully created the feed for horse',
    defaultErrorMessage: 'There was a problem adding the feed to this horse',
    responseName: 'newHorseFeed',
  });
  return promise;
};

export const updateHorseFeed = (id, feedId, request) => (dispatch) => {
  const promise = HorseService.updateHorseFeed(id, feedId, request);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.UPDATE_HORSE_FEED_REQUESTED,
    successType: types.UPDATE_HORSE_FEED_SUCCESS,
    errorType: types.UPDATE_HORSE_FEED_ERROR,
    promise,
    successMessage: 'Successfully updated the horse feed',
    defaultErrorMessage: 'There was a problem updating the horse feed',
    responseName: 'updatedHorseFeed',
  });
  return promise;
};

export const deleteHorseFeed = (id, feedId) => (dispatch) => {
  const promise = HorseService.deleteHorseFeed(id, feedId);
  dispatchNetworkRequestDynamicSuccessDispatch({
    dispatch,
    requestType: types.DELETE_HORSE_FEED_REQUESTED,
    successDispatch: {
      type: types.DELETE_HORSE_FEED_SUCCESS,
      deletedHorseId: id,
      deletedFeedId: feedId,
    },
    errorType: types.DELETE_HORSE_FEED_ERROR,
    promise,
    successMessage: 'Successfully deleted the horse feed',
    defaultErrorMessage: 'There was a problem deleting the horse feed',
    responseName: 'response',
  });
  return promise;
};

/* *********
  Horse Location Methods
********** */

export const getHorseLocations = (id, page = 0, pageSize = 25, sort = '') => (dispatch) => {
  const promise = HorseService.getHorseLocations(id, page, pageSize, sort);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_HORSE_LOCATIONS_REQUESTED,
    successType: types.GET_HORSE_LOCATIONS_SUCCESS,
    errorType: types.GET_HORSE_LOCATIONS_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem loading locations for this horse',
    responseName: 'horseLocations',
  });
  return promise;
};

export const addHorseLocation = (id, locationId, request, reloadAllHorseLocations = true) => (dispatch, getState) => {
  const {
    board: {
      startDate,
      endDate,
    },
  } = getState();
  const promise = LocationService.addHorseLocation(locationId, id, request);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.ADD_HORSE_LOCATION_REQUESTED,
    successType: types.ADD_HORSE_LOCATION_SUCCESS,
    errorType: types.ADD_HORSE_LOCATION_ERROR,
    promise,
    successMessage: 'Successfully created the location for horse',
    defaultErrorMessage: 'There was a problem adding the location for this horse',
    responseName: 'newHorseLocation',
  });
  if (reloadAllHorseLocations) {
    promise.then(() => {
      dispatch(getAllHorseLocations(startDate, endDate)); // reload all horse locations -- TODO need a better way - probably move to realtime updates?
    })
  }
  return promise;
};

export const updateHorseLocation = (id, locationId, request, horseLocationStartDate, reloadAllHorseLocations = true) => (dispatch, getState) => {
  const {
    board: {
      startDate,
      endDate,
    },
  } = getState();
  const promise = LocationService.updateHorseLocation(locationId, id, request, horseLocationStartDate);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.UPDATE_HORSE_LOCATION_REQUESTED,
    successType: types.UPDATE_HORSE_LOCATION_SUCCESS,
    errorType: types.UPDATE_HORSE_LOCATION_ERROR,
    promise,
    successMessage: 'Successfully updated the horse location',
    defaultErrorMessage: 'There was a problem updating the horse location',
    responseName: 'updatedHorseLocation',
  });
  if (reloadAllHorseLocations) {
    promise.then(() => {
      dispatch(getAllHorseLocations(startDate, endDate)); // reload all horse locations -- TODO need a better way - probably move to realtime updates?
    })
  } return promise;
};

export const deleteHorseLocation = (id, locationId, horseLocationStartDate, reloadAllHorseLocations = true) => (dispatch, getState) => {
  const {
    board: {
      startDate,
      endDate,
    },
  } = getState();
  const promise = LocationService.deleteHorseLocation(locationId, id, horseLocationStartDate);
  dispatchNetworkRequestDynamicSuccessDispatch({
    dispatch,
    requestType: types.DELETE_HORSE_LOCATION_REQUESTED,
    successDispatch: {
      type: types.DELETE_HORSE_LOCATION_SUCCESS,
      deletedHorseLocationId: id,
    },
    errorType: types.DELETE_HORSE_LOCATION_ERROR,
    promise,
    successMessage: 'Successfully deleted the horse location',
    defaultErrorMessage: 'There was a problem deleting the horse location',
    responseName: 'response',
  });
  if (reloadAllHorseLocations) {
    promise.then(() => {
      dispatch(getAllHorseLocations(startDate, endDate)); // reload all horse locations -- TODO need a better way - probably move to realtime updates?
    })
  } return promise;
};

/* *********
  Horse File Methods
********** */

export const addHorseFile = (
  id,
  fileId,
  file,
  clientAccess,
) => (dispatch) => {
  const promise = HorseService.addHorseFile(id, fileId, file, clientAccess);

  dispatchNetworkRequest({
    dispatch,
    requestType: types.ADD_HORSE_FILE_REQUESTED,
    successType: types.ADD_HORSE_FILE_SUCCESS,
    errorType: types.ADD_HORSE_FILE_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem uploading the file',
    successMessage: 'Successfully uploaded the file',
    responseName: 'newFile',
  });

  return promise;
};

export const getHorseFile = (id, fileId, fileName, downloadFile = false, publicHorseShareId = null) => (dispatch) => {
  const promise = publicHorseShareId
    ? HorsePublicShareService.getHorseFile(publicHorseShareId, fileId, fileName)
    : HorseService.getHorseFile(id, fileId, fileName);
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_HORSE_FILE_REQUESTED,
    successType: types.GET_HORSE_FILE_SUCCESS,
    errorType: types.GET_HORSE_FILE_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem downloading the file',
    responseName: 'download',
  });
  promise.then((download) => {
    if (downloadFile) {
      const blob = new Blob([download], { type: 'application/octet-stream' });
      FileDownload(blob, fileName);
    }
  });
  return promise;
};

/* *********
  Horse Suggestion Methods
********** */

export const getHorseSuggestion = (id) => (dispatch) => {
  const promise = HorseService.getHorseSuggestion(id)
  dispatchNetworkRequest({
    dispatch,
    requestType: types.GET_HORSE_SUGGESTION_REQUESTED,
    successType: types.GET_HORSE_SUGGESTION_SUCCESS,
    errorType: types.GET_HORSE_SUGGESTION_ERROR,
    promise,
    defaultErrorMessage: 'There was a problem loading suggestions for this horse',
    responseName: 'horseSuggestion',
  });
  return promise;
};

export const updateHorseSuggestion = (id, request) => (dispatch) => {
  const promise = SuggestionService.updateHorseSuggestion(id, request)
  dispatchNetworkRequest({
    dispatch,
    requestType: types.UPDATE_HORSE_SUGGESTION_REQUESTED,
    successType: types.UPDATE_HORSE_SUGGESTION_SUCCESS,
    errorType: types.UPDATE_HORSE_SUGGESTION_ERROR,
    promise,
    successMessage: 'Successfully updated the horse suggestion',
    defaultErrorMessage: 'There was a problem updating the horse suggestion',
    responseName: 'updatedHorseSuggestion',
  });
  return promise;
};
